import { useEffect, useState } from 'react'

import { AnimatedCursor, type CursorPosition } from './AnimatedCursor'

/**
 * The read: which panel, and how far down it, as a share of the distance to the CTA.
 * Alternating columns is the point — it should look like someone weighing one offer
 * against the other, not sweeping a single card.
 */
const TOUR: { column: 0 | 1; depth: number }[] = [
  { column: 1, depth: 0.26 },
  { column: 0, depth: 0.44 },
  { column: 1, depth: 0.62 },
  { column: 0, depth: 0.78 },
]

/** The first stop is held longer because the pointer is still flying in to reach it. */
const ENTRY_HOLD_MS = 600
const STOP_MS = 550
/** Shorter than a stop, so the pointer settles and reads before moving on. */
const TOUR_TRAVEL_MS = 360
/** The move that commits to a quote — longer, and across the whole viewer. */
const DECIDE_TRAVEL_MS = 520

interface CursorTourProps {
  /** Horizontal centre of each panel, in the panel row's own coordinates. */
  columnsX: readonly [number, number]
  /** Centre of the Add to cart button; also the depth the tour measures against. */
  ctaPosition: CursorPosition | null
  /** Still comparing. False once the pointer commits to the cart button. */
  touring: boolean
  pressed: boolean
}

/** Walks the pointer down between the two open quotes before it settles on Add to cart. */
export function CursorTour({ columnsX, ctaPosition, touring, pressed }: CursorTourProps) {
  const [stop, setStop] = useState(0)

  useEffect(() => {
    if (!touring || stop >= TOUR.length - 1) return
    const timer = window.setTimeout(
      () => setStop((current) => current + 1),
      stop === 0 ? ENTRY_HOLD_MS : STOP_MS
    )
    return () => clearTimeout(timer)
  }, [touring, stop])

  if (!ctaPosition) return null

  const { column, depth } = TOUR[stop]
  const position = touring ? { x: columnsX[column], y: ctaPosition.y * depth } : ctaPosition

  return (
    <AnimatedCursor
      position={position}
      pressed={pressed}
      travelMs={touring ? TOUR_TRAVEL_MS : DECIDE_TRAVEL_MS}
    />
  )
}
